import type { AppointmentStatus } from "@planerius/contracts"
import type { CabinetAppointmentView } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useId } from "react"

import { cx } from "../lib/cx"
import { formatDateLong, formatDayMonth } from "../lib/format"
import type { BadgeTone } from "../ui/badge"
import { Badge } from "../ui/badge"
import { Button, IconButton } from "../ui/button"
import { MaskedField } from "../ui/field"
import {
  appointmentHistory,
  canUndoNoShow,
  formatVersion,
  HISTORY_GAP_NOTE,
  panelActions,
} from "./appointment-history"
import { noShowLabel, visitsLabel } from "../lib/plural"
import styles from "./journal-actions.module.css"
import "./journal-tokens.css"
import { formatRange, minutesOfDay } from "./journal-time"
import { formatMoney, remainderMinor } from "./money"
import { statusVisual } from "./status-style"

/**
 * Панель записи 2-2.3.4.
 *
 * ЧТО ЭТО ЗА ЭКРАН. Сюда администратор приходит, когда с записью что-то не так: клиент
 * звонит, спорит, опаздывает, просит перенести. Поэтому панель отвечает на вопросы
 * в порядке их появления: кто это (и что о нём надо знать до начала визита), что он
 * заказал и сколько должен, что он просил, что с записью уже происходило.
 *
 * ПОЧЕМУ ПАНЕЛЬ, А НЕ ОТДЕЛЬНЫЙ ЭКРАН. Сетка за панелью остаётся видимой: «перенести
 * на 18:30» проверяется взглядом в соседнюю колонку. При этом у панели ЕСТЬ АДРЕС
 * (?appointment=<id>) — ссылку на открытую запись отправляют в поддержку, и она обязана
 * открываться у того, кому её прислали.
 *
 * ДЕНЬГИ И ТЕЛЕФОН ГАСИТ СЕРВЕР. priceMinor === null означает «этому человеку деньги
 * не показываем» (право finance.view либо настройка 4-Б12) — и тогда блока нет ВОВСЕ,
 * а не прочерк: прочерк читается как «визит бесплатный». То же с телефоном: приходит
 * маска, а полный номер — отдельным действием, которое пишется в аудит.
 */

const BADGE_TONE: Readonly<Record<AppointmentStatus, BadgeTone>> = {
  pending: "warn",
  confirmed: "ok",
  in_progress: "info",
  completed: "gray",
  cancelled: "gray",
  no_show: "error",
}

export interface PanelPermissions {
  /** appointments.status.set — приход, завершение, неявка и её снятие. */
  readonly status: boolean
  readonly reschedule: boolean
  readonly cancel: boolean
  /** appointments.create — «Продублировать» это повторное создание. */
  readonly create: boolean
  /** busy.manage — «Заблокировать время». */
  readonly busy: boolean
  /** clients.phone.view — иначе кнопки «Показать» нет. */
  readonly phone: boolean
}

export interface PanelCallbacks {
  readonly onClose: () => void
  readonly onConfirm: () => void
  readonly onArrived: () => void
  readonly onCompleted: () => void
  readonly onNoShow: () => void
  readonly onUndoNoShow: () => void
  readonly onReschedule: () => void
  readonly onCancel: () => void
  readonly onDuplicate: () => void
  readonly onRevealPhone: () => void
  readonly onCommentChange?: ((value: string) => void) | undefined
}

export function AppointmentPanel(props: {
  readonly appointment: CabinetAppointmentView
  readonly employeeName: string
  readonly branchName: string
  readonly timeZone: string
  /** Календарная дата дня журнала: от неё считаются минуты карточки. */
  readonly date: string
  /** «Сейчас» в миллисекундах — от него зависит, можно ли ещё снять неявку. */
  readonly now: number
  /** Можно ли менять день (офлайн, технические работы, режим поддержки). */
  readonly canEdit: boolean
  /** Почему нельзя. Уходит в title кнопки и в подпись для чтения с экрана. */
  readonly deniedReason: string | null
  readonly permissions: PanelPermissions
  /** Открыть диалог блокировки. undefined — кнопки нет вовсе. */
  readonly onBlockTime?: (() => void) | undefined
  readonly revealedPhone: string | null
  readonly revealing: boolean
  /** Какое действие сейчас выполняется: его кнопка показывает «Сохраняем…». */
  readonly busyAction: string | null
  readonly failure: string | null
  readonly callbacks: PanelCallbacks
}): ReactNode {
  const { appointment, callbacks } = props
  const visual = statusVisual(appointment.status)
  const commentId = useId()

  // Полный номер: либо он пришёл вместе с записью (право clients.phone.view),
  // либо его только что открыли действием — «Позвонить» работает в обоих случаях.
  const fullPhone = appointment.client.phone ?? props.revealedPhone

  /*
   * ЗАКРЫТЬ МОЖНО ТОЛЬКО ОСВОБОЖДЁННОЕ ВРЕМЯ. У отменённого визита занятость снята
   * (вместо неё стоит карантин, и его сервер снимает сам), а у неявки и завершённого
   * строка занятости ОСТАЁТСЯ: время отработано, это факт, и блокировка поверх него
   * упрётся в EXCLUDE. У живого визита время занято им самим.
   *
   * Неразобранная запись вовсе не занимает времени: закрывать нечьё время нечем —
   * у блокировки должен быть мастер или ресурс.
   */
  const blockDenied =
    appointment.employeeId === null
      ? "У записи нет мастера — закрывать нечьё время."
      : appointment.status === "cancelled"
        ? null
        : appointment.status === "no_show" || appointment.status === "completed"
          ? "Время визита остаётся за ним: он отработан, и закрывать его блокировкой не нужно."
          : "Это время занято визитом — сначала отмените или перенесите его."


  const startMinutes = minutesOfDay(new Date(appointment.startAt), props.timeZone)
  const endMinutes = minutesOfDay(new Date(appointment.endAt), props.timeZone)
  const undoAvailable = canUndoNoShow(appointment.noShowAt, props.now)
  const actions = panelActions(appointment.status, undoAvailable)

  const prepaid = appointment.prepaidMinor
  const price = appointment.priceMinor
  const rest = remainderMinor(price, prepaid)

  /*
   * «Права нет» и «сейчас нельзя» — РАЗНЫЕ причины, и путать их нельзя: первую человек
   * решает просьбой к владельцу, вторую — ожиданием связи. Право проверяется первым:
   * оно не зависит от режима и остаётся правдой, когда связь вернётся.
   */
  const reasonFor = (hasRight: boolean): string | undefined => {
    if (!hasRight) return "У вашей роли нет этого права. Попросите администратора салона."
    if (!props.canEdit) {
      return props.deniedReason ?? "Изменения сейчас недоступны."
    }
    return undefined
  }

  const history = appointmentHistory(appointment, props.timeZone)

  return (
    <aside
      className={styles["panel"]}
      role="complementary"
      aria-label={`Запись: ${appointment.client.displayName}`}
    >
      <header className={styles["panelHead"]}>
        <div className={styles["panelHeadText"]}>
          <h2 className={styles["panelTitle"]}>{appointment.client.displayName}</h2>
          <div className={styles["panelSubtitle"]}>
            {formatDateLong(props.date, props.timeZone)} · {formatRange(startMinutes, endMinutes)} ·{" "}
            {props.employeeName} · {props.branchName}
            {/* Кресло называется там же, где мастер: «Анна Соколова · кресло 2» (5-А3).
                Клиент спрашивает «а где я сижу», и ответ должен быть в первой строке. */}
            {appointment.resourceNames.length === 0
              ? ""
              : ` · ${appointment.resourceNames.join(", ")}`}
          </div>
        </div>
        <Badge tone={BADGE_TONE[appointment.status]}>{visual.name}</Badge>
        <IconButton label="Закрыть панель записи" icon="✕" onClick={callbacks.onClose} />
      </header>

      <div className={styles["panelBody"]}>
        {/* Закреплённая заметка идёт ПЕРВОЙ: аллергию читают до визита, а не после. */}
        {appointment.client.pinnedNotes.map((note) => (
          <div key={note} className={styles["note"]}>
            <span className={styles["noteMark"]} aria-hidden="true">
              !
            </span>
            <span>{note} — закреплённая заметка</span>
          </div>
        ))}

        {appointment.client.isBlacklisted ? (
          <div className={cx(styles["note"], styles["noteDanger"])}>
            <span className={styles["noteMark"]} aria-hidden="true">
              ⊘
            </span>
            <span>
              Клиенту ограничена онлайн-запись. По телефону записать по-прежнему можно —
              решение за салоном.
            </span>
          </div>
        ) : null}

        <div>
          <div className={styles["sectionLabel"]}>Клиент</div>
          <div className={styles["clientRow"]}>
            <div>
              {/*
               * Номер целиком — ДЕЙСТВИЕ, а не поле: каждый показ пишется в аудит,
               * и салон видит отчёт «кто сколько контактов открыл за неделю». Это
               * единственный работающий инструмент против тихого выноса базы глазами.
               */}
              {appointment.client.phone === null && appointment.client.phoneMasked !== null ? (
                <MaskedField
                  label="Телефон"
                  masked={appointment.client.phoneMasked}
                  revealed={props.revealedPhone ?? undefined}
                  revealLabel="Показать"
                  spoken="номер скрыт, откройте его кнопкой «Показать»"
                  revealing={props.revealing}
                  onReveal={callbacks.onRevealPhone}
                  deniedReason={
                    props.permissions.phone
                      ? undefined
                      : "Открывать номера клиентов может администратор салона."
                  }
                  hint="Открытие номера останется в журнале действий салона"
                />
              ) : appointment.client.phone === null ? (
                <div className={styles["clientStats"]}>Телефон не записан</div>
              ) : (
                <div className={styles["clientPhone"]}>{appointment.client.phone}</div>
              )}
              {/*
                * «Позвонить» — ссылка tel:, а не кнопка: на телефоне администратора это
                * основное действие карточки (5-А3), и звонок должен уходить одним
                * касанием, без промежуточного экрана. На настольном браузере ссылка
                * просто откроет привычное приложение звонков или ничего не сделает —
                * это честнее, чем прятать её от половины зрителей.
                */}
              {fullPhone === null ? null : (
                <a className={styles["callLink"]} href={`tel:${fullPhone}`}>
                  Позвонить
                </a>
              )}
              <div className={styles["clientStats"]}>
                {visitsLabel(appointment.client.visitsCount)} · {noShowLabel(appointment.client.noShowCount)}
                {appointment.client.lastVisitAt === null
                  ? ""
                  : ` · последний ${formatDayMonth(appointment.client.lastVisitAt, props.timeZone)}`}
              </div>
            </div>
            <div className={styles["clientButtons"]}>
              {/*
               * Ссылка, а не кнопка: карточка клиента — другой экран со своим адресом
               * (3-А2). Обычная ссылка переживает и «открыть в новой вкладке».
               */}
              <a className={styles["linkButton"]} href={`/clients/${appointment.client.id}`}>
                Карточка
              </a>
            </div>
          </div>
        </div>

        <div>
          <div className={styles["sectionLabel"]}>Состав визита</div>
          <div className={styles["items"]}>
            {appointment.services.map((service, index) => (
              <div className={styles["itemRow"]} key={`${service.serviceId}-${String(index)}`}>
                <div>
                  <div className={styles["itemName"]}>{service.name}</div>
                  <div className={styles["itemMeta"]}>
                    {service.durationMinutes} мин
                    {service.employeeId === null ? "" : ` · ${props.employeeName}`}
                  </div>
                </div>
                {service.priceMinor === null ? null : (
                  <div className={styles["itemPrice"]}>{formatMoney(service.priceMinor)}</div>
                )}
              </div>
            ))}

            {/* Итоговой строки НЕТ ВОВСЕ, когда деньги не показываются: см. money.ts. */}
            {price === null ? null : (
              <div className={cx(styles["itemRow"], styles["totalRow"])}>
                <div className={styles["totalLabel"]}>
                  Итого
                  {prepaid === null || prepaid === 0
                    ? ""
                    : ` · предоплата ${formatMoney(prepaid)}`}
                  {rest === null || prepaid === null || prepaid === 0
                    ? ""
                    : ` · остаток ${formatMoney(rest)}`}
                </div>
                <div className={styles["itemPrice"]}>{formatMoney(price)}</div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className={styles["sectionLabel"]} htmlFor={commentId}>
            Комментарий к записи
          </label>
          {/*
           * Поле только для чтения, пока правка состава не подключена: отдельного
           * маршрута у комментария нет, он меняется через PATCH вместе с составом визита
           * (право appointments.past.edit). Показать поле ввода, которое некуда сохранить,
           * значит потерять то, что человек в него напишет.
           */}
          <textarea
            id={commentId}
            className={styles["comment"]}
            value={appointment.comment ?? ""}
            readOnly={callbacks.onCommentChange === undefined}
            placeholder="Клиент ничего не просил"
            onChange={(event) => {
              callbacks.onCommentChange?.(event.currentTarget.value)
            }}
          />
        </div>

        <div>
          <div className={styles["sectionLabel"]}>История</div>
          <div className={styles["history"]}>
            {history.map((row) => (
              <div key={row}>{row}</div>
            ))}
            <div className={styles["historyVersion"]}>
              Версия записи: {formatVersion(appointment.updatedAt, props.timeZone)}
            </div>
          </div>
          <div className={styles["historyGap"]}>{HISTORY_GAP_NOTE}</div>
        </div>

        {props.failure === null ? null : (
          <div className={styles["failure"]} role="alert">
            {props.failure}
          </div>
        )}
      </div>

      <footer className={styles["panelFoot"]}>
        {actions.canConfirm ? (
          <Button
            size="sm"
            loading={props.busyAction === "confirm"}
            loadingLabel="Подтверждаем…"
            deniedReason={reasonFor(props.permissions.status)}
            onClick={callbacks.onConfirm}
          >
            Подтвердить
          </Button>
        ) : null}

        {actions.primary === "arrived" ? (
          <Button
            variant="primary"
            size="sm"
            loading={props.busyAction === "arrived"}
            loadingLabel="Отмечаем…"
            deniedReason={reasonFor(props.permissions.status)}
            onClick={callbacks.onArrived}
          >
            Клиент пришёл
          </Button>
        ) : null}

        {actions.primary === "completed" ? (
          <Button
            variant="primary"
            size="sm"
            loading={props.busyAction === "completed"}
            loadingLabel="Завершаем…"
            deniedReason={reasonFor(props.permissions.status)}
            onClick={callbacks.onCompleted}
          >
            Завершено
          </Button>
        ) : null}

        {actions.primary === "undo-no-show" ? (
          <Button
            variant="primary"
            size="sm"
            loading={props.busyAction === "no-show-undo"}
            loadingLabel="Снимаем…"
            deniedReason={reasonFor(props.permissions.status)}
            onClick={callbacks.onUndoNoShow}
          >
            Снять отметку неявки
          </Button>
        ) : null}

        {actions.canReschedule ? (
          <Button
            variant="secondary"
            size="sm"
            deniedReason={reasonFor(props.permissions.reschedule)}
            onClick={callbacks.onReschedule}
          >
            Перенести
          </Button>
        ) : null}

        {/*
          * «Заблокировать время» — про освободившееся окно отменённого визита: клиент
          * не придёт, и продавать этот час больше не нужно. У живого визита время занято
          * им самим, поэтому кнопка объясняет отказ, а не исчезает: исчезнувшая кнопка
          * читается как «в этой программе так нельзя» (ТЗ §2.11.4).
          */}
        {props.onBlockTime === undefined ? null : (
          <Button
            variant="secondary"
            size="sm"
            deniedReason={
              props.permissions.busy
                ? (blockDenied ?? reasonFor(true))
                : "Закрывать время может администратор салона."
            }
            onClick={props.onBlockTime}
          >
            Заблокировать время
          </Button>
        )}

        {actions.canDuplicate ? (
          <Button
            variant="secondary"
            size="sm"
            deniedReason={reasonFor(props.permissions.create)}
            onClick={callbacks.onDuplicate}
          >
            Продублировать
          </Button>
        ) : null}

        <span className={styles["footSpacer"]} />

        {actions.canCancel ? (
          <Button
            variant="danger-ghost"
            size="sm"
            deniedReason={reasonFor(props.permissions.cancel)}
            onClick={callbacks.onCancel}
          >
            Отменить
          </Button>
        ) : null}

        {actions.canNoShow ? (
          <Button
            variant="danger-ghost"
            size="sm"
            deniedReason={reasonFor(props.permissions.status)}
            onClick={callbacks.onNoShow}
          >
            Не пришёл
          </Button>
        ) : null}
      </footer>
    </aside>
  )
}
